import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatCurrency, monthLabel } from "@/lib/format";
import { ArrowUpRight, ArrowDownRight, Minus, GitCompareArrows } from "lucide-react";

function DeltaPill({ delta, invert = false, testId }) {
  // invert=true means "going down is good" (e.g. expenses).
  // invert=false means "going up is good" (e.g. income, balance).
  if (!delta) return null;
  const value = delta.absolute || 0;
  const pct = delta.percent;

  let tone = "neutral";
  if (Math.abs(value) < 0.01) tone = "neutral";
  else if (value > 0) tone = invert ? "bad" : "good";
  else tone = invert ? "good" : "bad";

  const palette = {
    good: { bg: "#D1FAE5", color: "#047857", Icon: ArrowDownRight },
    bad: { bg: "#FEE2E2", color: "#B91C1C", Icon: ArrowUpRight },
    neutral: { bg: "#F1F5F9", color: "#64748B", Icon: Minus },
  };
  const c = palette[tone];
  // Pick arrow direction by sign of value, not by tone
  const Arrow = value > 0 ? ArrowUpRight : value < 0 ? ArrowDownRight : Minus;
  const pctStr = pct === null || pct === undefined ? "Nuevo" : `${Math.abs(pct).toFixed(1)}%`;

  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium font-numeric"
      style={{ backgroundColor: c.bg, color: c.color }}
      data-testid={testId}
    >
      <Arrow className="w-3.5 h-3.5" />
      {pctStr}
    </span>
  );
}

function MetricRow({ label, current, previous, delta, currency, invert, testId }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-[#2A3447] last:border-b-0" data-testid={testId}>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-[0.18em] text-[#94A3B8] font-medium">{label}</p>
        <p className="font-display text-lg font-semibold text-[#F1F5F9] font-numeric mt-0.5" data-testid={`${testId}-current`}>
          {formatCurrency(current, currency)}
        </p>
        <p className="text-xs text-[#94A3B8] font-numeric">
          Mes pasado: {formatCurrency(previous, currency)}
        </p>
      </div>
      <div className="flex flex-col items-end gap-1">
        <DeltaPill delta={delta} invert={invert} testId={`${testId}-delta`} />
        <span className="text-xs text-[#64748B] font-numeric" data-testid={`${testId}-abs`}>
          {delta.absolute > 0 ? "+" : ""}
          {formatCurrency(delta.absolute, currency)}
        </span>
      </div>
    </div>
  );
}

export default function MonthlyComparisonCard({ month, year }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/summary/compare?month=${month}&year=${year}`)
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month, year]);

  const currency = data?.currency || "MXN";
  const toneBg = {
    success: "#D1FAE5",
    warning: "#FEF3C7",
    info: "#F1F5F9",
  };
  const toneColor = {
    success: "#047857",
    warning: "#B45309",
    info: "#64748B",
  };

  return (
    <div className="clay-card p-6 fade-up" data-testid="monthly-comparison-card">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#0052FF]/10">
            <GitCompareArrows className="w-4 h-4 text-[#0052FF]" />
          </div>
          <div>
            <h2 className="font-display text-xl font-semibold text-[#F1F5F9]">
              Compárate con el mes pasado
            </h2>
            {data && (
              <p className="text-xs text-[#94A3B8]">
                {monthLabel(data.month, data.year)} vs {monthLabel(data.previous_month, data.previous_year)}
              </p>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-[#94A3B8] py-6 text-center">Calculando comparación…</p>
      ) : !data ? (
        <p className="text-sm text-[#94A3B8] py-6 text-center">
          No se pudo cargar la comparación.
        </p>
      ) : (
        <>
          <div
            className="rounded-lg px-4 py-3 mb-4 text-sm font-medium"
            style={{
              backgroundColor: toneBg[data.tone] || toneBg.info,
              color: toneColor[data.tone] || toneColor.info,
            }}
            data-testid="comparison-headline"
          >
            {data.headline}
          </div>

          <div>
            <MetricRow
              label="Ingresos"
              current={data.current.total_income}
              previous={data.previous.total_income}
              delta={data.delta.income}
              currency={currency}
              invert={false}
              testId="compare-income"
            />
            <MetricRow
              label="Gastos"
              current={data.current.total_expense}
              previous={data.previous.total_expense}
              delta={data.delta.expense}
              currency={currency}
              invert={true}
              testId="compare-expense"
            />
            <MetricRow
              label="Saldo"
              current={data.current.balance}
              previous={data.previous.balance}
              delta={data.delta.balance}
              currency={currency}
              invert={false}
              testId="compare-balance"
            />
          </div>

          {data.top_category_changes && data.top_category_changes.length > 0 && (
            <div className="mt-5 pt-4 border-t border-[#2A3447]" data-testid="compare-top-changes">
              <p className="text-xs uppercase tracking-[0.18em] text-[#94A3B8] font-medium mb-3">
                Categorías con mayor variación
              </p>
              <div className="space-y-2">
                {data.top_category_changes.map((c, i) => {
                  const isNew = c.previous === 0;
                  const tone = c.absolute > 0 ? "bad" : c.absolute < 0 ? "good" : "neutral";
                  const color =
                    tone === "good" ? "#047857" : tone === "bad" ? "#B91C1C" : "#64748B";
                  return (
                    <div
                      key={c.category}
                      className="flex items-center justify-between text-sm"
                      data-testid={`compare-top-change-${i}`}
                    >
                      <span className="text-[#F1F5F9] font-medium truncate">{c.category}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-numeric text-[#64748B] text-xs">
                          {formatCurrency(c.previous, currency)} → {formatCurrency(c.current, currency)}
                        </span>
                        <span
                          className="font-numeric font-semibold text-xs px-2 py-0.5 rounded"
                          style={{ color, backgroundColor: `${color}15` }}
                        >
                          {isNew
                            ? "Nuevo"
                            : `${c.absolute > 0 ? "+" : ""}${formatCurrency(c.absolute, currency)}`}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
